import { MeshData, Color, emptyMesh, flatShade, merge } from './mesh';

const WHITE: Color = [1, 1, 1];

function pushVert(m: MeshData, x: number, y: number, z: number, nx: number, ny: number, nz: number, c: Color): number {
  m.positions.push(x, y, z);
  m.normals.push(nx, ny, nz);
  m.colors.push(c[0], c[1], c[2]);
  m.emissive.push(0, 0, 0);
  return m.positions.length / 3 - 1;
}

/** Axis-aligned box centered at origin. Already flat-shaded (24 verts). */
export function box(w: number, h: number, d: number, color: Color = WHITE): MeshData {
  const m = emptyMesh();
  const hx = w / 2, hy = h / 2, hz = d / 2;
  const faces: [number[], number[]][] = [
    [[1, 0, 0], [hx, -hy, -hz, hx, hy, -hz, hx, hy, hz, hx, -hy, hz]],
    [[-1, 0, 0], [-hx, -hy, hz, -hx, hy, hz, -hx, hy, -hz, -hx, -hy, -hz]],
    [[0, 1, 0], [-hx, hy, -hz, -hx, hy, hz, hx, hy, hz, hx, hy, -hz]],
    [[0, -1, 0], [-hx, -hy, hz, -hx, -hy, -hz, hx, -hy, -hz, hx, -hy, hz]],
    [[0, 0, 1], [-hx, -hy, hz, hx, -hy, hz, hx, hy, hz, -hx, hy, hz]],
    [[0, 0, -1], [hx, -hy, -hz, -hx, -hy, -hz, -hx, hy, -hz, hx, hy, -hz]],
  ];
  for (const [n, v] of faces) {
    const base = m.positions.length / 3;
    for (let i = 0; i < 4; i++) pushVert(m, v[i * 3], v[i * 3 + 1], v[i * 3 + 2], n[0], n[1], n[2], color);
    m.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
  return m;
}

/** UV sphere. Faceted by default; pass flat=false for smooth normals. */
export function sphere(r: number, segs = 10, rings = 7, color: Color = WHITE, flat = true): MeshData {
  const m = emptyMesh();
  for (let j = 0; j <= rings; j++) {
    const v = j / rings, phi = v * Math.PI;
    for (let i = 0; i <= segs; i++) {
      const u = i / segs, th = u * Math.PI * 2;
      const nx = Math.sin(phi) * Math.cos(th), ny = Math.cos(phi), nz = Math.sin(phi) * Math.sin(th);
      pushVert(m, nx * r, ny * r, nz * r, nx, ny, nz, color);
    }
  }
  for (let j = 0; j < rings; j++) for (let i = 0; i < segs; i++) {
    const a = j * (segs + 1) + i, b = a + segs + 1;
    if (j !== 0) m.indices.push(a, a + 1, b);
    if (j !== rings - 1) m.indices.push(a + 1, b + 1, b);
  }
  return flat ? flatShade(m) : m;
}

/** Cylinder along Y, centered at origin. rTop may be 0 for a cone. */
export function cylinder(rTop: number, rBot: number, h: number, segs = 10, color: Color = WHITE, capped = true, flat = true): MeshData {
  const m = emptyMesh();
  const hh = h / 2;
  const slope = (rBot - rTop) / h;
  const nl = Math.hypot(1, slope);
  for (let i = 0; i <= segs; i++) {
    const th = (i / segs) * Math.PI * 2, c = Math.cos(th), s = Math.sin(th);
    pushVert(m, c * rTop, hh, s * rTop, c / nl, slope / nl, s / nl, color);
    pushVert(m, c * rBot, -hh, s * rBot, c / nl, slope / nl, s / nl, color);
  }
  for (let i = 0; i < segs; i++) {
    const a = i * 2, b = a + 1, c = a + 2, d = a + 3;
    if (rTop > 0) m.indices.push(a, c, b);
    if (rBot > 0) m.indices.push(b, c, d);
  }
  if (capped) {
    if (rTop > 0) {
      const center = pushVert(m, 0, hh, 0, 0, 1, 0, color);
      for (let i = 0; i < segs; i++) {
        const th0 = (i / segs) * Math.PI * 2, th1 = ((i + 1) / segs) * Math.PI * 2;
        const v0 = pushVert(m, Math.cos(th0) * rTop, hh, Math.sin(th0) * rTop, 0, 1, 0, color);
        const v1 = pushVert(m, Math.cos(th1) * rTop, hh, Math.sin(th1) * rTop, 0, 1, 0, color);
        m.indices.push(center, v1, v0);
      }
    }
    if (rBot > 0) {
      const center = pushVert(m, 0, -hh, 0, 0, -1, 0, color);
      for (let i = 0; i < segs; i++) {
        const th0 = (i / segs) * Math.PI * 2, th1 = ((i + 1) / segs) * Math.PI * 2;
        const v0 = pushVert(m, Math.cos(th0) * rBot, -hh, Math.sin(th0) * rBot, 0, -1, 0, color);
        const v1 = pushVert(m, Math.cos(th1) * rBot, -hh, Math.sin(th1) * rBot, 0, -1, 0, color);
        m.indices.push(center, v0, v1);
      }
    }
  }
  return flat ? flatShade(m) : m;
}

export function cone(r: number, h: number, segs = 10, color: Color = WHITE): MeshData {
  return cylinder(0, r, h, segs, color, true);
}

/** Capsule along Y: cylinder of `height` (between hemisphere centers) with radius r. Total height = height + 2r. */
export function capsule(r: number, height: number, segs = 10, rings = 4, color: Color = WHITE): MeshData {
  const m = emptyMesh();
  const hh = height / 2;
  const rows: number[] = []; // y offset and phi per row
  // top hemisphere rows (phi 0..pi/2), then bottom hemisphere rows (phi pi/2..pi)
  for (let j = 0; j <= rings; j++) rows.push(j);
  for (let j = rings; j <= 2 * rings; j++) rows.push(j);
  for (let k = 0; k < rows.length; k++) {
    const j = rows[k];
    const phi = (j / (2 * rings)) * Math.PI;
    const off = k <= rings ? hh : -hh;
    for (let i = 0; i <= segs; i++) {
      const th = (i / segs) * Math.PI * 2;
      const nx = Math.sin(phi) * Math.cos(th), ny = Math.cos(phi), nz = Math.sin(phi) * Math.sin(th);
      pushVert(m, nx * r, ny * r + off, nz * r, nx, ny, nz, color);
    }
  }
  const ringCount = rows.length;
  for (let j = 0; j < ringCount - 1; j++) for (let i = 0; i < segs; i++) {
    const a = j * (segs + 1) + i, b = a + segs + 1;
    if (j !== 0) m.indices.push(a, a + 1, b);
    if (j !== ringCount - 2) m.indices.push(a + 1, b + 1, b);
  }
  return flatShade(m);
}

/** Partial torus in the XY plane, sweeping `arc` radians from +X counter-clockwise. */
export function torusSegment(R: number, r: number, arc = Math.PI * 2, segs = 24, tubeSegs = 8, color: Color = WHITE, capped = true): MeshData {
  const m = emptyMesh();
  for (let i = 0; i <= segs; i++) {
    const u = (i / segs) * arc, cu = Math.cos(u), su = Math.sin(u);
    for (let j = 0; j <= tubeSegs; j++) {
      const v = (j / tubeSegs) * Math.PI * 2, cv = Math.cos(v), sv = Math.sin(v);
      const x = (R + r * cv) * cu, y = (R + r * cv) * su, z = r * sv;
      pushVert(m, x, y, z, cv * cu, cv * su, sv, color);
    }
  }
  for (let i = 0; i < segs; i++) for (let j = 0; j < tubeSegs; j++) {
    const a = i * (tubeSegs + 1) + j, b = a + tubeSegs + 1;
    m.indices.push(a, b, a + 1, a + 1, b, b + 1);
  }
  if (capped && arc < Math.PI * 2 - 1e-6) {
    const ends: [number, boolean][] = [[0, false], [arc, true]];
    for (const [ang, flip] of ends) {
      const cu = Math.cos(ang), su = Math.sin(ang);
      const nx = flip ? -su : su, ny = flip ? cu : -cu;
      const center = pushVert(m, R * cu, R * su, 0, nx, ny, 0, color);
      for (let j = 0; j < tubeSegs; j++) {
        const v0 = (j / tubeSegs) * Math.PI * 2, v1 = ((j + 1) / tubeSegs) * Math.PI * 2;
        const p0 = pushVert(m, (R + r * Math.cos(v0)) * cu, (R + r * Math.cos(v0)) * su, r * Math.sin(v0), nx, ny, 0, color);
        const p1 = pushVert(m, (R + r * Math.cos(v1)) * cu, (R + r * Math.cos(v1)) * su, r * Math.sin(v1), nx, ny, 0, color);
        if (flip) m.indices.push(center, p1, p0); else m.indices.push(center, p0, p1);
      }
    }
  }
  return flatShade(m);
}
export function torus(R: number, r: number, segs = 24, tubeSegs = 8, color: Color = WHITE): MeshData {
  return torusSegment(R, r, Math.PI * 2, segs, tubeSegs, color, false);
}

/** Flat plane in XZ facing +Y, centered, optionally subdivided. */
export function plane(w: number, d: number, nx = 1, nz = 1, color: Color = WHITE): MeshData {
  const m = emptyMesh();
  for (let j = 0; j <= nz; j++) for (let i = 0; i <= nx; i++) {
    pushVert(m, (i / nx - 0.5) * w, 0, (j / nz - 0.5) * d, 0, 1, 0, color);
  }
  for (let j = 0; j < nz; j++) for (let i = 0; i < nx; i++) {
    const a = j * (nx + 1) + i, b = a + nx + 1;
    m.indices.push(a, b, a + 1, a + 1, b, b + 1);
  }
  return m;
}

/** Extrude a closed 2D polygon (XZ, winding such that (p1-p0) x (p2-p0) points +Y) along +Y from y=0 to y=h. Convex only. */
export function prism(points: [number, number][], h: number, color: Color = WHITE): MeshData {
  const m = emptyMesh();
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const [x0, z0] = points[i], [x1, z1] = points[(i + 1) % n];
    const ex = x1 - x0, ez = z1 - z0, l = Math.hypot(ex, ez) || 1;
    // outward normal for CCW (viewed from +Y, i.e. looking down -Y): right-hand of edge
    const nx = -ez / l, nz = ex / l;
    const base = m.positions.length / 3;
    pushVert(m, x0, 0, z0, nx, 0, nz, color); pushVert(m, x1, 0, z1, nx, 0, nz, color);
    pushVert(m, x1, h, z1, nx, 0, nz, color); pushVert(m, x0, h, z0, nx, 0, nz, color);
    m.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
  const caps: [number, number][] = [[h, 1], [0, -1]];
  for (const [y, ny] of caps) {
    const base = m.positions.length / 3;
    for (const [x, z] of points) pushVert(m, x, y, z, 0, ny, 0, color);
    for (let i = 1; i < n - 1; i++) {
      if (ny > 0) m.indices.push(base, base + i, base + i + 1); else m.indices.push(base, base + i + 1, base + i);
    }
  }
  return m;
}

/** Regular polygon prism helper (e.g. octagonal pillars), base at y=0. */
export function polygonPrism(r: number, h: number, sides: number, color: Color = WHITE): MeshData {
  const pts: [number, number][] = [];
  for (let i = 0; i < sides; i++) { const a = (i / sides) * Math.PI * 2; pts.push([Math.cos(a) * r, -Math.sin(a) * r]); }
  return prism(pts, h, color);
}

/** Merge many meshes into a new one. */
export function combine(...meshes: MeshData[]): MeshData { return merge(emptyMesh(), ...meshes); }
